/**
 * AerationConfigPage.tsx
 *
 * Per-scenario aeration configuration. Reads the currently active scenario
 * from sessionStorage (the same store the Train Summary page uses), exposes
 * every aeration-relevant parameter for adjustment, lets the user persist
 * the changes, and provides a one-click "Apply & return to Train Summary"
 * action that re-loads the scenario into the simulator so they immediately
 * see the new health-score, KPI and compliance numbers.
 *
 * Works for every scenario type (preset, benchmark, perfect, custom).
 */

import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Wind, ArrowLeft, Save as SaveIcon, RotateCcw, Sparkles, Wrench,
  ChevronRight, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import {
  getActiveScenario, activateScenario, saveCustomScenario, makeCustomScenario,
  loadCustomScenarios, type ScenarioInputs,
} from '@/lib/scenarioManager';
import { assessReservoirHealth } from '@/lib/reservoirHealth';
import { computeDevicePerformance, type PerfectDiffuserSpec } from '@/lib/perfectDiffuser';

const STORAGE_KEY = 'rb_aeration_extras_v1';

interface AerationExtras {
  /** Number of diffusers in array. */
  count: number;
  /** Layout. */
  layout: 'hex' | 'square' | 'line' | 'deep-cluster';
  spacingFactor: number;
  deepZoneBias: number;
  dutyMode: 'continuous' | 'day-only' | 'pulsed-30min' | 'seasonal';
  dutyPercent: number;
  poreSize_um: number;
  membrane: 'EPDM' | 'silicone' | 'ceramic-dome';
  discDiameter_mm: number;
  capexPerDevice: number;
  heightAboveBed_m: number;
}

const DEFAULT_EXTRAS: AerationExtras = {
  count: 14,
  layout: 'hex',
  spacingFactor: 1.8,
  deepZoneBias: 0.7,
  dutyMode: 'continuous',
  dutyPercent: 100,
  poreSize_um: 1500,
  membrane: 'EPDM',
  discDiameter_mm: 300,
  capexPerDevice: 2200,
  heightAboveBed_m: 0.5,
};

function loadExtrasForScenario(scenarioId: string): AerationExtras {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_EXTRAS;
    const map = JSON.parse(raw) as Record<string, AerationExtras>;
    return { ...DEFAULT_EXTRAS, ...(map[scenarioId] ?? {}) };
  } catch { return DEFAULT_EXTRAS; }
}

function saveExtrasForScenario(scenarioId: string, extras: AerationExtras): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, AerationExtras>) : {};
    map[scenarioId] = extras;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (e) { console.warn('Could not save aeration extras', e); }
}

export default function AerationConfigPage() {
  const navigate = useNavigate();
  const active = getActiveScenario();
  const scenarioId = active?.scenarioId ?? active?.siteId ?? 'default';
  const scenarioName = active?.siteName ?? 'Default scenario';

  // Aeration parameters that ARE in ScenarioInputs (drive the simulator directly)
  const [bubble_d_mm, setBubble] = useState(active?.inputs?.bubble_d_mm ?? 2.5);
  const [diffuserQair_Ls, setQair] = useState(active?.inputs?.diffuserQair_Ls ?? 2.0);
  const [diffuserDepth_m, setDepth] = useState(active?.inputs?.diffuserDepth_m ?? 4.0);

  // Aeration parameters NOT in ScenarioInputs (stored as per-scenario extras)
  const [extras, setExtras] = useState<AerationExtras>(() => loadExtrasForScenario(scenarioId));

  // Snapshot of the scenario's baseline so we can compare before/after
  const baselineParams = useMemo(() => ({ ...(active?.inputs ?? {}) } as ScenarioInputs | null), [active?.scenarioId, active?.siteId]);
  const [snapshotHealthScore] = useState<number | null>(() => baselineParams ? assessReservoirHealth(baselineParams).overallScore : null);

  // Live performance preview using the Perfect Diffuser performance engine
  const liveSpec: PerfectDiffuserSpec = useMemo(() => ({
    bubble_d_mm, poreSize_um: extras.poreSize_um, membrane: extras.membrane,
    discDiameter_mm: extras.discDiameter_mm,
    Qair_perDevice_Ls: diffuserQair_Ls,
    releaseDepth_m: diffuserDepth_m,
    heightAboveBed_m: extras.heightAboveBed_m,
    count: extras.count,
    layout: extras.layout,
    spacingFactor: extras.spacingFactor,
    deepZoneBias: extras.deepZoneBias,
    dutyMode: extras.dutyMode,
    dutyPercent: extras.dutyPercent,
    capexPerDevice: extras.capexPerDevice,
  }), [bubble_d_mm, diffuserQair_Ls, diffuserDepth_m, extras]);

  const perf = useMemo(() => computeDevicePerformance(liveSpec, active?.inputs?.area_m2 ?? 34000), [liveSpec, active?.inputs?.area_m2]);

  // Live health re-score with the new aeration parameters
  const liveHealth = useMemo(() => {
    if (!baselineParams) return null;
    return assessReservoirHealth({ ...baselineParams, bubble_d_mm, diffuserQair_Ls, diffuserDepth_m });
  }, [baselineParams, bubble_d_mm, diffuserQair_Ls, diffuserDepth_m]);

  // Persist extras as user adjusts
  useEffect(() => { saveExtrasForScenario(scenarioId, extras); }, [extras, scenarioId]);

  const applyAndReturn = () => {
    if (!active || !baselineParams) {
      alert('No active scenario found. Open Train Summary first to load one.');
      navigate('/scenarios');
      return;
    }
    const updatedInputs: ScenarioInputs = {
      ...baselineParams,
      bubble_d_mm, diffuserQair_Ls, diffuserDepth_m,
    };
    // Find or create the saved custom version
    const existing = loadCustomScenarios().find(s => s.id === scenarioId);
    const saved = existing
      ? { ...existing, inputs: updatedInputs, updatedAt: Date.now() }
      : makeCustomScenario(`${scenarioName} (aeration-adjusted)`, updatedInputs, { trainId: 'shallow_uk_eutrophic', description: 'Aeration configuration adjusted via Aeration Config page.' });
    saveCustomScenario(saved);
    activateScenario(saved);
    navigate('/train-summary/shallow_uk_eutrophic');
  };

  const resetToBaseline = () => {
    if (!baselineParams) return;
    setBubble(baselineParams.bubble_d_mm);
    setQair(baselineParams.diffuserQair_Ls);
    setDepth(baselineParams.diffuserDepth_m);
    setExtras(DEFAULT_EXTRAS);
  };

  const loadPerfectDiffuserDefaults = () => {
    setBubble(2.0);
    setQair(2.0);
    setDepth(4.0);
    setExtras({
      count: 16, layout: 'hex', spacingFactor: 1.8, deepZoneBias: 0.7,
      dutyMode: 'continuous', dutyPercent: 100,
      poreSize_um: 1500, membrane: 'EPDM', discDiameter_mm: 300,
      capexPerDevice: 2200, heightAboveBed_m: 0.5,
    });
  };

  // Delta indicators
  const baselineHealth = baselineParams ? assessReservoirHealth(baselineParams).overallScore : 0;
  const delta = liveHealth ? liveHealth.overallScore - baselineHealth : 0;
  const deltaColour = delta > 1 ? 'text-emerald-300' : delta < -1 ? 'text-red-300' : 'text-slate-300';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Wind} title="Aeration Configuration"
        subtitle={`Adjust the aeration setup for the currently active scenario - ${scenarioName}`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={applyAndReturn}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-white">
              <ChevronRight className="w-4 h-4" /> Apply & return to Train Summary
            </button>
            <button onClick={resetToBaseline}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <RotateCcw className="w-4 h-4" /> Reset
            </button>
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>
        }
      />

      {!active && (
        <div className="rounded-xl border-2 border-amber-700 bg-amber-900/15 p-4 text-sm text-amber-100">
          <strong>No active scenario.</strong> Open a scenario first (Scenario Manager &rarr; "Load &amp; analyse") and come back here.
        </div>
      )}

      {/* Headline strip - baseline vs live, with health delta */}
      {active && (
        <section className="rounded-2xl border-2 border-sky-700/40 bg-slate-900 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="rounded-lg border border-slate-700 bg-slate-950/40 p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500">Scenario baseline</div>
              <div className="text-lg font-bold text-slate-100">{scenarioName}</div>
              <div className="text-xs text-slate-400 mt-1">Health: <strong className="text-slate-100">{snapshotHealthScore?.toFixed(0) ?? '-'}</strong> / 100</div>
            </div>
            <div className="rounded-lg border-2 border-emerald-700/40 bg-emerald-900/15 p-3">
              <div className="text-xs uppercase tracking-wider text-emerald-300">Live with new aeration</div>
              <div className="text-2xl font-bold text-emerald-200">{liveHealth?.overallScore.toFixed(0) ?? '-'} / 100</div>
              <div className="text-xs mt-1">
                Delta: <strong className={deltaColour}>{delta >= 0 ? '+' : ''}{delta.toFixed(1)}</strong>
              </div>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-950/40 p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500">Active aeration method</div>
              <div className="text-sm font-bold text-slate-100">Reynolds &amp; Bauhm Diffuser</div>
              <div className="text-xs text-slate-400 mt-1">{extras.membrane} - {extras.count} devices - {extras.layout} layout</div>
            </div>
          </div>
        </section>
      )}

      {/* Quick load defaults */}
      <section className="rounded-xl border border-slate-700 bg-slate-900 p-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-400">
          <strong className="text-slate-200">Quick start: </strong>
          load the published "Perfect Diffuser" defaults (16 devices, 2.0 mm bubbles, EPDM, hex grid)
          tuned for Barcombe-class polymictic reservoirs.
        </div>
        <button onClick={loadPerfectDiffuserDefaults}
          className="inline-flex items-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-500 px-3 py-1.5 text-xs font-semibold text-white">
          <Sparkles className="w-3.5 h-3.5" /> Load Perfect Diffuser defaults
        </button>
      </section>

      {/* Device design - sliders that DIRECTLY affect the simulator */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-3 flex items-center gap-2">
          <Wrench className="w-4 h-4 text-sky-300" /> Device design (drives simulator directly)
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <Slider label="Bubble release diameter" unit="mm" min={0.5} max={5.0} step={0.1}
            value={bubble_d_mm} onChange={setBubble}
            help="Fine-bubble 1-3 mm gives best OTE." />
          <Slider label="Air per device" unit="L/s" min={0.3} max={6.0} step={0.1}
            value={diffuserQair_Ls} onChange={setQair}
            help="2.0 L/s = sweet spot for fine-bubble." />
          <Slider label="Release depth (below surface)" unit="m" min={1.0} max={7.5} step={0.1}
            value={diffuserDepth_m} onChange={setDepth} />
        </div>
      </section>

      {/* Layout & array - additional configuration */}
      <section className="rounded-2xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-base font-bold text-slate-100 mb-3 flex items-center gap-2">
          <Wind className="w-4 h-4 text-emerald-300" /> Array layout &amp; operating regime
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <Slider label="Number of devices" unit="" min={4} max={48} step={1}
            value={extras.count} onChange={v => setExtras({ ...extras, count: v })} />
          <Select label="Layout pattern" value={extras.layout}
            options={[['hex','Hex staggered'],['square','Square grid'],['line','Single line'],['deep-cluster','Deep-zone cluster']]}
            onChange={v => setExtras({ ...extras, layout: v })} />
          <Slider label="Spacing factor" unit="x plume" min={1.0} max={3.0} step={0.1}
            value={extras.spacingFactor} onChange={v => setExtras({ ...extras, spacingFactor: v })} />
          <Slider label="Deep-zone bias" unit="0-1" min={0} max={1} step={0.05}
            value={extras.deepZoneBias} onChange={v => setExtras({ ...extras, deepZoneBias: v })} />
          <Slider label="Height above bed" unit="m" min={0.1} max={3.0} step={0.1}
            value={extras.heightAboveBed_m} onChange={v => setExtras({ ...extras, heightAboveBed_m: v })} />
          <Select label="Duty mode" value={extras.dutyMode}
            options={[['continuous','Continuous'],['day-only','Day-only (12h)'],['pulsed-30min','Pulsed 30-min'],['seasonal','Seasonal %']]}
            onChange={v => setExtras({ ...extras, dutyMode: v })} />
          {extras.dutyMode === 'seasonal' && (
            <Slider label="Duty cycle" unit="%" min={5} max={100} step={5}
              value={extras.dutyPercent} onChange={v => setExtras({ ...extras, dutyPercent: v })} />
          )}
          <Slider label="Pore size" unit="um" min={300} max={3000} step={50}
            value={extras.poreSize_um} onChange={v => setExtras({ ...extras, poreSize_um: v })} />
          <Select label="Membrane material" value={extras.membrane}
            options={[['EPDM','EPDM (10 yr life)'],['silicone','Silicone (12 yr)'],['ceramic-dome','Ceramic dome (20 yr)']]}
            onChange={v => setExtras({ ...extras, membrane: v })} />
          <Slider label="Disc diameter" unit="mm" min={150} max={500} step={10}
            value={extras.discDiameter_mm} onChange={v => setExtras({ ...extras, discDiameter_mm: v })} />
          <Slider label="CAPEX per device" unit="GBP" min={500} max={6000} step={100}
            value={extras.capexPerDevice} onChange={v => setExtras({ ...extras, capexPerDevice: v })} />
        </div>
      </section>

      {/* Live performance preview */}
      <section className="rounded-2xl border border-sky-700/40 bg-sky-950/15 p-4">
        <div className="text-base font-bold text-slate-100 mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-sky-300" /> Live performance preview
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <PerfCard k="Effective OTE" v={`${perf.ote_pct.toFixed(0)} %`} target="> 25 %" ok={perf.ote_pct > 25} />
          <PerfCard k="Plume diameter" v={`${perf.plumeDiameter_m.toFixed(1)} m`} target="-" ok />
          <PerfCard k="O2 / device" v={`${perf.o2PerDevice_kgDay.toFixed(1)} kg/d`} target="-" ok />
          <PerfCard k="Array total O2" v={`${perf.o2Total_kgDay.toFixed(0)} kg/d`} target="-" ok />
          <PerfCard k="Power total" v={`${perf.powerTotal_kW.toFixed(1)} kW`} target="-" ok />
          <PerfCard k="GBP per kg O2" v={`GBP  ${perf.costPerKgO2.toFixed(2)}`} target="< 1.5" ok={perf.costPerKgO2 < 1.5} />
          <PerfCard k="Plume footprint"         v={`${(perf.coverageFraction * 100).toFixed(1)} %`} target="1.5 - 15 %" ok={perf.coverageFraction >= 0.015 && perf.coverageFraction <= 0.15} />
          <PerfCard k="Surface mixing coverage" v={`${(perf.surfaceMixingCoverage * 100).toFixed(0)} %`} target="> 80 %" ok={perf.surfaceMixingCoverage > 0.8} />
          <PerfCard k="CAPEX (array)" v={`GBP  ${(perf.capex_GBP / 1000).toFixed(0)} k`} target="-" ok />
        </div>
      </section>

      {/* Help and references */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <strong className="text-slate-200">How this works.</strong> The three "device design" sliders directly modify the simulator's
        aeration inputs (bubble diameter, air flow, release depth) - these are stored as part of the scenario and drive every
        downstream metric (OTE, plume coverage, O2 supply, days to anoxia, health score, etc.). The "array &amp; operating regime"
        block stores additional configuration (number of devices, layout, duty cycle, membrane material) that the Perfect Diffuser
        engine uses to compute the live performance preview above. When you click <em>Apply &amp; return to Train Summary</em> the
        scenario is saved (as a custom scenario if it wasn't already) and the Train Summary page reloads with the new aeration
        configuration in place - you'll see the updated Health Scorecard, KPI table and compliance verdicts immediately.
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------

function Slider({ label, unit, min, max, step, value, onChange, help }: {
  label: string; unit: string; min: number; max: number; step: number;
  value: number; onChange: (v: number) => void; help?: string;
}) {
  return (
    <div>
      <label className="text-slate-400 block mb-1">
        {label}: <strong className="text-slate-100">{Number.isInteger(step) ? value : value.toFixed(2)} {unit}</strong>
      </label>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full accent-sky-500" />
      {help && <div className="text-[10px] text-slate-500 italic mt-0.5">{help}</div>}
    </div>
  );
}

function Select<T extends string>({ label, value, options, onChange }: {
  label: string; value: T; options: Array<[T, string]>; onChange: (v: T) => void;
}) {
  return (
    <div>
      <label className="text-slate-400 block mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value as T)}
        className="w-full rounded-md border border-slate-700 bg-slate-800 px-2 py-1.5 text-xs text-slate-200">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function PerfCard({ k, v, target, ok }: { k: string; v: string; target: string; ok: boolean }) {
  const Icon = ok ? CheckCircle2 : AlertTriangle;
  return (
    <div className={`rounded-xl border p-3 ${ok ? 'border-emerald-700/60 bg-emerald-900/15' : 'border-amber-700/60 bg-amber-900/15'}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
        <Icon className={`w-3 h-3 ${ok ? 'text-emerald-400' : 'text-amber-400'}`} /> {k}
      </div>
      <div className={`text-lg font-bold mt-1 ${ok ? 'text-emerald-200' : 'text-amber-200'}`}>{v}</div>
      {target !== '-' && <div className="text-[10px] text-slate-500">target {target}</div>}
    </div>
  );
}

export const _save = SaveIcon;  // tree-shaker keep
